import { CSSProperties, useRef } from 'react';
import { getPlatformName } from '@shared/utils/platformName';
import { TransportMode } from '@shared/models/transportMode';
import styles from './DepartureOrArrivalTablePlatformCell.module.scss';
import classNames from 'classnames';
import useElementSize from '@shared/hooks/useElementSize';

interface DepartureOrArrivalTablePlatformCellProps {
  originalPlatform?: string;
  platform?: string;
  transportMode: TransportMode;
  headerWidth?: number;
}

export default function DepartureOrArrivalTablePlatformCell({
  originalPlatform,
  platform,
  transportMode,
  headerWidth,
}: DepartureOrArrivalTablePlatformCellProps) {
  const platformName = getPlatformName(transportMode);
  const originalPlatformRef = useRef<HTMLSpanElement>(null);
  const originalPlatformSize = useElementSize(originalPlatformRef, {
    renderRef: originalPlatform !== undefined,
  });
  const crossoverLength =
    originalPlatformSize.width !== undefined &&
    originalPlatformSize.height !== undefined
      ? Math.sqrt(
          Math.pow(originalPlatformSize.width, 2) +
            Math.pow(originalPlatformSize.height, 2),
        )
      : 0;
  const crossoverAngle =
    originalPlatformSize.width !== undefined &&
    originalPlatformSize.height !== undefined
      ? Math.atan2(originalPlatformSize.height, originalPlatformSize.width)
      : 0;
  return (
    <td className={styles['platform-cell']} style={{ minWidth: headerWidth }}>
      <div className={styles.platforms}>
        {platform && (
          <span
            className={classNames(
              styles.platform,
              originalPlatform && styles['platform--updated'],
            )}
          >
            <span
              className={classNames(
                styles.platform__text,
                platform.length > 1 &&
                  styles['platform__text--multi-characters'],
              )}
            >
              <span aria-hidden="true">{platform}</span>
              <span className="sr-only">
                {originalPlatform
                  ? `Nytt ${platformName} ${platform}`
                  : platform}
              </span>
            </span>
          </span>
        )}
        {originalPlatform && (
          <span
            ref={originalPlatformRef}
            className={classNames(
              styles.platform,
              styles['platform--previous'],
            )}
            style={
              {
                '--crossover-length': `${crossoverLength}px`,
                '--crossover-angle': `${crossoverAngle}rad`,
              } as CSSProperties
            }
          >
            <span
              className={classNames(
                styles.platform__text,
                originalPlatform.length > 1 &&
                  styles['platform__text--multi-characters'],
              )}
            >
              <span aria-hidden="true">{originalPlatform}</span>
              <span className="sr-only">
                {`Ändrat från ${platformName} ${originalPlatform}`}
              </span>
            </span>
          </span>
        )}
      </div>
    </td>
  );
}
